import { useId } from 'react';
import type { FieldErrors } from './servicePageDraft';

/**
 * Spec 081a — editable list of short lines ("What's included"): add, remove, and reorder with
 * Move up / Move down buttons (no drag-only reordering). Each line has its own maxLength and
 * shows its counter once it gets close to the limit. Errors are read from `errors[errorKey]`
 * (the whole list) and `errors[`${errorKey}[i]`]` (one line).
 */
export interface LineListEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  lang: 'en' | 'ar';
  maxItems: number;
  maxLength: number;
  errorKey: string;
  errors: FieldErrors;
  placeholder?: string;
  addLabel?: string;
  testId?: string;
}

export default function LineListEditor({
  label, items, onChange, lang, maxItems, maxLength, errorKey, errors, placeholder, addLabel = '+ Add item', testId,
}: LineListEditorProps) {
  const id = useId();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const listError = errors[errorKey];
  const full = items.length >= maxItems;

  const setItem = (i: number, value: string) => onChange(items.map((it, j) => (j === i ? value : it)));
  const removeItem = (i: number) => onChange(items.filter((_, j) => j !== i));
  const moveItem = (i: number, delta: number) => {
    const to = i + delta;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  };
  const addItem = () => { if (!full) onChange([...items, '']); };

  return (
    <fieldset className="svc-list" data-testid={testId} aria-describedby={listError ? `${id}-error` : undefined}>
      <legend className="svc-list__legend">{label}</legend>
      {items.length === 0 && <p className="svc-list__empty">No items yet.</p>}
      <ol className="svc-list__items">
        {items.map((item, i) => {
          const itemError = errors[`${errorKey}[${i}]`];
          const inputId = `${id}-item-${i}`;
          const showCounter = item.length >= Math.floor(maxLength * 0.8);
          return (
            <li key={i} className="svc-list__item">
              <div className="svc-list__row">
                <input
                  id={inputId}
                  type="text"
                  value={item}
                  dir={dir}
                  lang={lang}
                  maxLength={maxLength}
                  placeholder={i === 0 ? placeholder : undefined}
                  aria-label={`${label} — item ${i + 1}`}
                  aria-invalid={itemError ? true : undefined}
                  aria-describedby={itemError ? `${inputId}-error` : undefined}
                  onChange={(e) => setItem(i, e.target.value)}
                />
                <div className="svc-row-actions">
                  <button type="button" className="svc-icon-btn" disabled={i === 0}
                    aria-label={`Move item ${i + 1} up`} title="Move up" onClick={() => moveItem(i, -1)}>↑</button>
                  <button type="button" className="svc-icon-btn" disabled={i === items.length - 1}
                    aria-label={`Move item ${i + 1} down`} title="Move down" onClick={() => moveItem(i, 1)}>↓</button>
                  <button type="button" className="svc-icon-btn svc-icon-btn--danger"
                    aria-label={`Remove item ${i + 1}`} title="Remove" onClick={() => removeItem(i)}>×</button>
                </div>
              </div>
              {(itemError || showCounter) && (
                <div className="svc-field__meta">
                  {itemError ? <span id={`${inputId}-error`} className="svc-field__error" role="alert">{itemError}</span> : <span />}
                  {showCounter && (
                    <span className={`svc-field__counter${item.length >= maxLength ? ' svc-field__counter--limit' : ''}`}>
                      {item.length}/{maxLength}
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="svc-list__footer">
        <button type="button" className="v2-add-btn v2-add-btn-small svc-add-btn" onClick={addItem} disabled={full}>
          {addLabel}
        </button>
        <span className={`svc-field__counter${full ? ' svc-field__counter--limit' : ''}`}>{items.length}/{maxItems} items</span>
      </div>
      {listError && <p id={`${id}-error`} className="svc-field__error" role="alert">{listError}</p>}
    </fieldset>
  );
}
